import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useUsersStore } from '../../store/useUsersStore';
import { formatUnixToPakistanTime, getUnixTimestamp } from '../../utils/dateUtils';

const OPTIONS_SECONDS = [
    { value: 60, label: '1 min' },
    { value: 120, label: '2 min' },
    { value: 300, label: '5 min' },
];

const AUTO_INTERVAL_MINUTES = [
    { value: '', label: '-- default / none --' },
    { value: 3, label: '3 min' },
    { value: 5, label: '5 min' },
    { value: 10, label: '10 min' },
];

const AutoBidSettingsFilter = ({ subUserKey = null }) => {
    const users = useUsersStore((s) => s.users);
    const selectedKey = useUsersStore((s) => s.selectedKey);
    const updateSubUser = useUsersStore((s) => s.updateSubUser);
    const fetchUsers = useUsersStore((s) => s.fetchUsers);

    const user = useMemo(() => {
        const key = subUserKey ?? selectedKey;
        if (!key || !Array.isArray(users)) return null;
        return users.find((u) => {
            return [
                u.document_id, u.documentId, u.sub_user_id, u.subUserId, u.id,
                u.sub_username, u.subUsername, u.username, u.email,
            ].some((c) => typeof c !== 'undefined' && c !== null && String(c) === String(key));
        }) || null;
    }, [users, selectedKey, subUserKey]);

    const [fetchUnder, setFetchUnder] = useState('');
    const [autoInterval, setAutoInterval] = useState('');
    const [dailyBid, setDailyBid] = useState('');
    const [dailyBidLimit, setDailyBidLimit] = useState('');
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState('');
    const [err, setErr] = useState('');
    const initialRef = useRef(null);

    useEffect(() => {
        setMsg('');
        setErr('');
        if (!user) {
            setFetchUnder('');
            setAutoInterval('');
            setDailyBid('');
            setDailyBidLimit('');
            initialRef.current = {
                fetchUnder: '',
                autoInterval: '',
                dailyBid: '',
                dailyBidLimit: '',
            };

            return;
        }
        // display: fetch_projects_under is in seconds;
        setFetchUnder(user.fetch_projects_under != null ? String(user.fetch_projects_under) : '');
        // convert backend seconds -> minutes for dropdown if it matches one of our options
        const backendSeconds = Number(user.auto_bid_interval);
        const minutes = Number.isFinite(backendSeconds) && backendSeconds > 0 ? Math.round(backendSeconds / 60) : '';
        // only set if matches allowed options (3,5,10), otherwise default/none
        setAutoInterval([3, 5, 10].includes(minutes) ? String(minutes) : '');
        const db = user.daily_bid ?? user.dailyBid ?? user.dailyBidCount ?? null;
        const dbl = user.daily_bid_limit ?? user.dailyBidLimit ?? user.daily_bid_limit ?? null;
        setDailyBid(db != null && db !== '' ? String(Number(db)) : '');
        setDailyBidLimit(dbl != null && dbl !== '' ? String(Number(dbl)) : '');
        // capture initial values for reset / dirty-check
        initialRef.current = {
            fetchUnder: user.fetch_projects_under != null ? String(user.fetch_projects_under) : '',
            autoInterval: [3, 5, 10].includes(minutes) ? String(minutes) : '',
            dailyBid: db != null && db !== '' ? String(Number(db)) : '',
            dailyBidLimit: dbl != null && dbl !== '' ? String(Number(dbl)) : '',
        };



    }, [user]);

    // last bid helpers
    const getLastBidSeconds = (val) => {
        if (val == null || val === '') return null;
        const n = Number(val);
        if (Number.isNaN(n)) return null;
        // normalize ms -> seconds if value looks like milliseconds
        if (n > 1e12) return Math.floor(n / 1000);
        return Math.floor(n);
    };

    const formatRelative = (unixSeconds) => {
        if (!unixSeconds) return 'Never';
        const now = getUnixTimestamp(); // seconds
        const diff = Math.max(0, now - Number(unixSeconds));
        if (diff < 60) return 'just now';
        if (diff < 3600) return `${Math.floor(diff / 60)} minute${Math.floor(diff / 60) === 1 ? '' : 's'} ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)} hour${Math.floor(diff / 3600) === 1 ? '' : 's'} ago`;
        return `${Math.floor(diff / 86400)} day${Math.floor(diff / 86400) === 1 ? '' : 's'} ago`;
    };

    const isDirty = useMemo(() => {
        const init = initialRef.current || {};
        return (
            String(init.fetchUnder || '') !== String(fetchUnder || '') ||
            String(init.autoInterval || '') !== String(autoInterval || '') ||
            String(init.dailyBid || '') !== String(dailyBid || '') ||
            String(init.dailyBidLimit || '') !== String(dailyBidLimit || '')
        );
    }, [fetchUnder, autoInterval, dailyBid, dailyBidLimit]);


    const handleSave = async () => {
        setErr('');
        setMsg('');
        if (!user) {
            setErr('No sub-user selected');
            return;
        }
        // parse inputs
        const fb = fetchUnder === '' ? null : Number(fetchUnder); // already seconds
        const aiMinutes = autoInterval === '' ? null : Number(autoInterval); // selected minutes from dropdown
        if (fb != null && Number.isNaN(fb)) { setErr('Invalid fetch_projects_under'); return; }
        if (aiMinutes != null && Number.isNaN(aiMinutes)) { setErr('Invalid auto_bid_interval'); return; }
        // convert minutes -> seconds for backend
        const aiSeconds = aiMinutes != null ? Math.max(0, Math.floor(aiMinutes * 60)) : null;


        // parse daily numeric fields
        const dbVal = dailyBid === '' ? null : Number(dailyBid);
        const dblVal = dailyBidLimit === '' ? null : Number(dailyBidLimit);
        if (dbVal != null && (Number.isNaN(dbVal) || dbVal < 0)) { setErr('Invalid daily_bid'); return; }
        if (dblVal != null && (Number.isNaN(dblVal) || dblVal < 0)) { setErr('Invalid daily_bid_limit'); return; }

        const payload = {};
        if (fb != null) payload.fetch_projects_under = fb;
        if (aiSeconds != null) payload.auto_bid_interval = aiSeconds;
        if (dbVal != null) payload.daily_bid = dbVal;
        if (dblVal != null) payload.daily_bid_limit = dblVal;
        if (Object.keys(payload).length === 0) {
            setErr('No changes to save');
            return;
        }

        setLoading(true);
        try {
            // pass user object — store.updateSubUser resolves canonical id internally
            await updateSubUser(user, payload);
            // // refresh store from backend (keeps UI consistent)
            // try { await fetchUsers(user.parent_uid || user.parentUid || undefined); } catch (_) { /* ignore */ }
            setMsg('Settings saved');
            // refresh initial snapshot after save
            initialRef.current = {
                fetchUnder: fetchUnder,
                autoInterval: autoInterval,
                dailyBid: dailyBid,
                dailyBidLimit: dailyBidLimit,
            };
        } catch (e) {
            setErr(e?.message || 'Failed to save settings');
        } finally {
            setLoading(false);
        }
    };

    if (!user) return null;
    // derive last bid displays
    const rawLast = user.last_bid_time ?? user.lastBidTime ?? null;
    const lastBidSec = getLastBidSeconds(rawLast);
    const lastBidFormatted = lastBidSec ? formatUnixToPakistanTime(lastBidSec) : 'Never';
    const lastBidRelative = lastBidSec ? formatRelative(lastBidSec) : '';


    return (
        <div className="p-3 bg-white rounded shadow-sm max-w-md">
            <div className="mb-2 font-semibold">Auto-bid settings for {user.sub_username}</div>
            {/* Last bid info */}
            <div className="mb-3">
                <label className="block text-xs text-gray-500">Last bid</label>
                <div className="text-sm text-gray-700">{lastBidFormatted}{lastBidRelative ? ` — ${lastBidRelative}` : ''}</div>
            </div>


            <label className="block text-sm text-gray-600 mb-1">Fetch projects under</label>
            <select value={fetchUnder} onChange={(e) => setFetchUnder(e.target.value)} className="border px-2 py-1 rounded mb-3 w-full">
                <option value="">-- default / none --</option>
                {OPTIONS_SECONDS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <div className="text-xs text-gray-500 mb-3">Project fetched under this time.</div>
            <label className="block text-sm text-gray-600 mb-1">
                Auto bids interval
            </label>

            <select
                value={autoInterval}
                onChange={(e) => setAutoInterval(e.target.value)}
                className="border px-2 py-1 rounded mb-2 w-full"
            >
                {AUTO_INTERVAL_MINUTES.map(opt => (
                    <option key={opt.value} value={opt.value}>
                        {opt.label}
                    </option>
                ))}
            </select>

            <div className="text-xs text-gray-500 mb-2">
                You can place a new bid after <strong>{autoInterval} minutes</strong>.
                <br />
                Each bid must have a minimum gap of <strong>{autoInterval} minutes</strong> between them.
            </div>


            <label className="block text-sm text-gray-600 mb-2">Daily bid (count)</label>
            <input
                type="number"
                min="0"
                value={dailyBid}
                onChange={(e) => setDailyBid(e.target.value)}
                placeholder="e.g. 1"
                className="border px-2 py-1 rounded mb-3 w-full"
                disabled={loading}
            />
            <div className="text-xs text-gray-500 mb-3">Number of bids to place per day.</div>

            <label className="block text-sm text-gray-600 mb-1">Daily bid limit</label>
            <input
                type="number"
                min="0"
                value={dailyBidLimit}
                onChange={(e) => setDailyBidLimit(e.target.value)}
                placeholder="e.g. 10"
                className="border px-2 py-1 rounded mb-3 w-full"
                disabled={loading}
            />
            <div className="text-xs text-gray-500 mb-3">Maximum number of bids allowed per day.</div>



            <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={loading || !isDirty}
                    className={`px-3 py-1 rounded text-white ${loading || !isDirty ? 'bg-gray-300' : 'bg-blue-600'}`}
                >
                    {loading ? 'Saving...' : 'Save'}
                </button>
                {/* <button
                  type="button"
                  onClick={() => {
                    // reset to captured initial snapshot if available, otherwise fallback to current user values
                    const init = initialRef.current || {
                      fetchUnder: user.fetch_projects_under ?? '',
                      autoInterval: (() => {
                        const backendSeconds = Number(user.auto_bid_interval);
                        const minutes = Number.isFinite(backendSeconds) && backendSeconds > 0 ? Math.round(backendSeconds / 60) : '';
                        return [3, 5, 10].includes(minutes) ? String(minutes) : '';
                      })(),
                      dailyBid: user.daily_bid ?? user.dailyBid ?? user.dailyBidCount ?? '',
                      dailyBidLimit: user.daily_bid_limit ?? user.dailyBidLimit ?? user.daily_bid_limit ?? '',
                    };
                    setFetchUnder(init.fetchUnder ?? '');
                    setAutoInterval(init.autoInterval ?? '');
                    setDailyBid(String(init.dailyBid ?? ''));
                    setDailyBidLimit(String(init.dailyBidLimit ?? ''));
                    setErr(''); setMsg('');
                }} className="px-3 py-1 bg-gray-100 rounded" >
                    Reset
                </button> */}
            </div>

            {msg && <div className="mt-2 text-sm text-green-600">{msg}</div>}
            {err && <div className="mt-2 text-sm text-red-600">{err}</div>}
        </div>
    );
};

export default AutoBidSettingsFilter;